import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { workbenchRequest } from "@/components/branch-cash/cashWorkbench";
import type { PaymentTermCode } from "@shared/payment-due-date";

export type PaymentPayer = "branch" | "finance";
export type SetupValues = { paymentTermCode: PaymentTermCode; paymentPayer: PaymentPayer; paymentResponsibleUserId: string | null };
export type PaymentSetup = SetupValues & {
  revision: number; paymentSetupConfirmedAt: string | null; canManage: boolean;
  exceptions: (Omit<SetupValues,"paymentTermCode"> & { branchId: string; paymentTermCode: PaymentTermCode | null })[];
  branches: { id: string; name: string }[]; users: { id: string; name: string }[];
};
export const paymentUrl = (path: string, workspaceId: string) => `/api/${path}${path.includes("?") ? "&" : "?"}workspaceId=${encodeURIComponent(workspaceId)}`;
export function usePaymentData<T>(path: string, enabled = true) {
  const { currentWorkspace, user } = useAuth();
  const workspaceId = currentWorkspace?.id || "";
  return useQuery<T>({ queryKey: ["payment-setup", workspaceId, user?.id, path], enabled: !!workspaceId && enabled,
    queryFn: () => workbenchRequest<T>("GET", paymentUrl(path, workspaceId)) });
}
export function usePaymentAction<TBody, TResult = unknown>(path: string, method = "POST") {
  const { currentWorkspace } = useAuth();
  const client = useQueryClient();
  return useMutation<TResult, Error, TBody>({
    mutationFn: body => workbenchRequest<TResult>(method, paymentUrl(path, currentWorkspace?.id || ""), body),
    onSuccess: () => { for (const key of ["payment-setup", "branch-cash", "cash-where", "/api/supplier-receipts", "/api/suppliers"]) void client.invalidateQueries({ queryKey: [key] }); },
  });
}
